import React, { useState, useEffect, useRef } from 'react';
import { FaBars, FaUsers, FaUserFriends, FaUserPlus, FaFilter, FaTimes, FaClock, FaExclamationTriangle, FaUser, FaCube } from 'react-icons/fa';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './styles/crmHeader.css';

const PRODUCTS = [
  { id: 'website', label: 'Hjemmeside', icon: '🌐' },
  { id: 'video', label: 'Video', icon: '🎥' },
  { id: 'marketing', label: 'Marketing', icon: '📢' },
  { id: 'automation', label: 'Automatisering', icon: '⚡' }
];

function CRMHeader({ onToggleMenu, activeFilter, onFilterChange, onAddCustomer }) {
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    owner: '',
    products: [],
    status: '',
    urgency: '' // 'warning' eller 'critical' for opdateringer
  });
  
  // Tilføj ref til filter dropdown
  const filtersRef = useRef(null);
  const filterButtonRef = useRef(null);

  // Tilføj click-outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (showFilters && 
          filtersRef.current && 
          !filtersRef.current.contains(event.target) &&
          !filterButtonRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersData);
      } catch (error) {
        console.error('Fejl ved hentning af brugere:', error);
      }
    };

    fetchUsers();
  }, []);

  const handleFilterChange = (type, value) => {
    const newFilters = { ...activeFilters };
    
    if (type === 'products') {
      if (newFilters.products.includes(value)) {
        newFilters.products = newFilters.products.filter(p => p !== value);
      } else {
        newFilters.products = [...newFilters.products, value];
      }
    } else {
      if (newFilters[type] === value) {
        newFilters[type] = '';
      } else {
        newFilters[type] = value;
      }
    }
    
    setActiveFilters(newFilters);
    onFilterChange('custom', newFilters);
  };

  const clearFilters = () => {
    setActiveFilters({
      owner: '',
      products: [],
      status: '',
      urgency: ''
    });
    onFilterChange('all');
  };

  const getActiveFilterCount = () => {
    return Object.values(activeFilters).reduce((count, value) => {
      if (Array.isArray(value)) {
        return count + value.length;
      }
      return count + (value ? 1 : 0);
    }, 0);
  };

  return (
    <div className="crm-header-container">
      <div className="header-left">
        <button className="menu-toggle" onClick={onToggleMenu}>
          <FaBars />
        </button>
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => onFilterChange('all')}
          >
            <FaUsers />
            Alle kunder
          </button>
          <button 
            className={`filter-btn ${activeFilter === 'mine' ? 'active' : ''}`}
            onClick={() => onFilterChange('mine')}
          >
            <FaUserFriends />
            Mine kunder
          </button>
        </div>
        
        <div className="advanced-filters">
          <button 
            ref={filterButtonRef}
            className={`filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            Filtre
            {getActiveFilterCount() > 0 && (
              <span className="filter-count">{getActiveFilterCount()}</span>
            )}
          </button>

          {showFilters && (
            <div ref={filtersRef} className="filters-dropdown">
              <div className="filters-header">
                <h3>Filtre</h3>
                {getActiveFilterCount() > 0 && (
                  <button className="clear-filters" onClick={clearFilters}>
                    <FaTimes />
                    Nulstil
                  </button>
                )}
              </div>

              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>
                    <FaUser />
                    Ejer
                  </h4>
                  {activeFilters.owner && (
                    <span className="active-count">1 aktiv</span>
                  )}
                </div>
                <div className="filter-options">
                  {users.map(user => (
                    <button
                      key={user.id}
                      className={`filter-option ${activeFilters.owner === user.id ? 'active' : ''}`}
                      onClick={() => handleFilterChange('owner', user.id)}
                    >
                      {user.photoURL ? (
                        <img src={user.photoURL} alt="" className="user-avatar" />
                      ) : (
                        <div className="user-avatar-placeholder">
                          {user.name ? user.name.charAt(0) : '?'}
                        </div>
                      )}
                      <span>{user.name || user.email}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>
                    <FaCube />
                    Produkter
                  </h4>
                  {activeFilters.products.length > 0 && (
                    <span className="active-count">{activeFilters.products.length} aktive</span>
                  )}
                </div>
                <div className="filter-options">
                  {PRODUCTS.map(product => (
                    <button
                      key={product.id}
                      className={`filter-option ${activeFilters.products.includes(product.id) ? 'active' : ''}`}
                      onClick={() => handleFilterChange('products', product.id)}
                    >
                      <span className="product-icon">{product.icon}</span>
                      <span>{product.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>
                    <FaClock />
                    Opdateringer
                  </h4>
                  {activeFilters.urgency && (
                    <span className="active-count">1 aktiv</span>
                  )}
                </div>
                <div className="filter-options">
                  <button
                    className={`filter-option warning ${activeFilters.urgency === 'warning' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('urgency', 'warning')}
                  >
                    <FaClock />
                    <span>Over 7 dage</span>
                  </button>
                  <button
                    className={`filter-option critical ${activeFilters.urgency === 'critical' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('urgency', 'critical')}
                  >
                    <FaExclamationTriangle />
                    <span>Over 14 dage</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <button className="add-customer-btn" onClick={onAddCustomer}>
        <FaUserPlus />
        Tilføj ny kunde
      </button>
    </div>
  );
}

export default CRMHeader;
